// Feed deep-link intent — the profile hero ("Give kudos", "Share your first
// post") hands off into the feed composer via hash query params:
//
//   #/feed?kudos=<email>   → open the composer in KUDOS mode with that person
//                            prefilled as the kudos target
//   #/feed?compose=1       → focus the composer (plain post mode)
//
// FeedHubView parses the hash through this helper on mount + hashchange, then
// scrubs the query (clearHashQuery) so refresh/back never re-fire the intent.
// Pure + framework-free so the profile→feed interlink contract is unit-tested
// (see feedIntent.test.ts + docs/REGRESSIONS.md).

export type FeedIntent = {
  /** Kudos target email (decoded), or null when absent/junk. */
  kudos: string | null
  /** True when the composer should be focused for a plain post. */
  compose: boolean
}

const NO_INTENT: FeedIntent = { kudos: null, compose: false }

// Light email shape-check — enough to reject junk params without being
// pedantic about RFC edge cases (targets are always company addresses).
// URL metacharacters are excluded so trailing hash/query junk can't ride in.
const EMAIL_RE = /^[^\s@?&#/]+@[^\s@?&#/]+\.[^\s@?&#/]+$/

export function parseFeedIntent(hash: string): FeedIntent {
  const raw = String(hash || '')
  // '#/feed?kudos=x' | '#feed?kudos=x' | '/feed?kudos=x' → 'feed?kudos=x'
  const cleaned = raw.replace(/^#/, '').replace(/^\//, '')
  const qIdx = cleaned.indexOf('?')
  const path = (qIdx >= 0 ? cleaned.slice(0, qIdx) : cleaned).split('/').filter(Boolean)
  // Intent params only apply on the feed route itself.
  if (path[0] !== 'feed') return NO_INTENT
  if (qIdx < 0) return NO_INTENT

  let params: URLSearchParams
  try {
    params = new URLSearchParams(cleaned.slice(qIdx + 1))
  } catch {
    return NO_INTENT
  }

  const kudosRaw = (params.get('kudos') || '').trim()
  const kudos = EMAIL_RE.test(kudosRaw) ? kudosRaw : null

  const composeRaw = (params.get('compose') || '').trim().toLowerCase()
  const compose = composeRaw === '1' || composeRaw === 'true'

  return { kudos, compose }
}
